import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTabs from '@/components/PricingTabs';
import TestimonialSlider from '@/components/TestimonialSlider';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Shield, Award, Users, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from 'sonner';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { submitLead, countryCodes, getDefaultCountryCode } from '@/services/api';

export default function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    country_code: '+1',
    message: '',
  });

  useEffect(() => {
    getDefaultCountryCode().then((code) => {
      setFormData((prev) => ({ ...prev, country_code: code }));
    });
  }, []);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await submitLead(formData, {
      source: 'home_hero',
      pageUrl: window.location.href,
      packageTitle: 'Essentials',
      packageAmount: 45,
      tmSignUpType: 1,
      onSuccess: (data) => {
        if (data.customerId) {
          window.location.href = `/thanks?customerid=${data.customerId}`;
        }
      },
    });

    if (result.success) {
      setFormData({
        name: '',
        email: '',
        contact_number: '',
        country_code: '+1',
        message: '',
      });
    }

    setLoading(false);
  };

  const features = [
    {
      icon: Shield,
      title: 'Complete Protection',
      description: 'Secure your brand with comprehensive trademark registration and monitoring services.',
      image: '/assets/img/photo-1676312210846-104b89aafd81.jpg',
    },
    {
      icon: Award,
      title: 'Expert Guidance',
      description: 'Our experienced team provides personalized support throughout the entire registration process.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Get a dedicated case manager who ensures your trademark application is handled with care.',
      image: '/assets/img/photo-1541746972996-4e0b0f43e02a.jpg',
    },
    {
      icon: Clock,
      title: 'Fast Processing',
      description: 'Quick turnaround times with priority service options to get your trademark registered faster.',
      image: 'https://images.unsplash.com/photo-1612442377759-b56787227b1b?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
  ];

  const services = [
    {
      title: 'Trademark Registration',
      description: 'Protect your brand name, logo, and slogan with official trademark registration. Our comprehensive service ensures your intellectual property is fully secured.',
      link: '/services/trademark-registration',
      image: '/assets/img/photo-1634562876572-5abe57afcceb.jpg',
    },
    {
      title: 'Trademark Search',
      description: 'Conduct a comprehensive trademark search before filing. Identify potential conflicts and ensure your brand name is available for registration.',
      link: '/services/trademark-search',
      image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxvZmZpY2V8ZW58MHx8fHwxNzYyNzk3MzU2fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Copyright Registration',
      description: 'Safeguard your creative works including literary, artistic, and digital content with official copyright protection.',
      link: '/services/copyright-registration',
      image: '/assets/img/pexels-photo-7693223.jpg',
    },
    {
      title: 'Company Formation',
      description: 'Establish your business entity with our comprehensive company formation services. Choose the right structure for your business.',
      link: '/services/company-formation',
      image: 'https://images.unsplash.com/photo-1758518731224-100f6a30bffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGRvY3VtZW50c3xlbnwwfHx8fDE3NjI3OTczNTB8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Amazon Brand Registry',
      description: 'Protect your brand on Amazon with our specialized brand registry assistance and enhanced selling tools.',
      link: '/services/amazon-brand-registry',
      image: 'https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxvZmZpY2V8ZW58MHx8fHwxNzYyNzk3MzU2fDA&ixlib=rb-4.1.0&q=85',
    },
  ];

  const [featuresRef, featuresVisible] = useScrollAnimation(0.1);
  const [servicesRef, servicesVisible] = useScrollAnimation(0.1);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navigation />

      {/* Hero Section with Creative Form */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-12 overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/assets/img/photo-1553639558-fb2e565066f5.jpg)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 via-[#06214c]/90 to-[#2C1810]/80"></div>
          {/* Decorative Elements */}
          <div className="absolute top-20 left-10 w-96 h-96 bg-[#D4A574]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#06214c]/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-left">
              {/* Badge */}
              
              
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#D4A574] mb-6 leading-tight"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="hero-title"
              >
                Lock Down Your Brand Identity
              </h1>
              <p className="text-lg text-white/90 mb-4" data-testid="hero-subtitle">
                Don’t leave your business vulnerable. We help you secure your trademarks quickly and confidently, giving you complete peace of mind. Trusted by entrepreneurs and established companies across the U.S
              </p>
              <div className="mb-6">
                <ul className="list-disc list-inside text-white/90">
                  <li>Trusted by top entrepreneurs </li>
                  <li>Comprehensive brand coverage </li>
                  <li>Easy step-by-step process</li>
                </ul>
                
              </div>
              
              <button
                onClick={() => setShowDialog(true)}
                className="bg-[#bf8a60] text-white px-12 py-2.5 rounded-full text-sm font-semibold hover:bg-[#a0733e] transition-all transform hover:scale-105 shadow-md"
              >
                Get Protected
              </button>
              {/* Trust Indicators */}
              {/* <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <CheckCircle className="text-[#D4A574]" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">98% Success</p>
                    <p className="text-white/70 text-sm">Approval Rate</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Clock className="text-[#D4A574]" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Fast Process</p>
                    <p className="text-white/70 text-sm">Quick Turnaround</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Users className="text-[#D4A574]" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Expert Team</p>
                    <p className="text-white/70 text-sm">24/7 Support</p>
                  </div>
                </div>
              </div> */}
            </div>

            {/* Right Side - Creative Form */}
            <div className="relative" data-testid="hero-form">
              {/* Decorative background */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4A574]/20 to-[#06214c]/20 rounded-3xl blur-2xl"></div>
              
              <div className="relative bg-[#06214c] backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/50">
                <div className="mb-6 text-center">
                  <h2 className="text-3xl font-bold text-[#D4A574] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Stop Someone Stealing Your Brand
                  </h2>
                 <p className='text-white text-base'>Claim your trademark now and lock in your business identity before it’s too late.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="hero-name" className="text-[#D4A574]">Name</Label>
                    <Input
                      id="hero-name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-gray-300 bg-white focus:border-[#06214c] focus:ring-[#06214c]"
                      data-testid="hero-form-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero-email" className="text-[#D4A574]">Email</Label>
                    <Input
                      id="hero-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="border-gray-300 bg-white focus:border-[#06214c] focus:ring-[#06214c]"
                      data-testid="hero-form-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hero-contact" className="text-[#D4A574]">Contact Number</Label>
                    <div className="flex space-x-2">
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger className="w-[120px] bg-white border-gray-300" data-testid="hero-form-country-code">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {countryCodes.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="hero-contact"
                        type="tel"
                        placeholder="123-456-7890"
                        value={formData.contact_number}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({ ...formData, contact_number: value });
                        }}
                        maxLength={10}
                        pattern="[0-9]*"
                        required
                        className="flex-1 border-gray-300 bg-white focus:border-[#06214c] focus:ring-[#06214c]"
                        data-testid="hero-form-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="hero-message" className="text-[#D4A574]">Message</Label>
                    <Textarea
                      id="hero-message"
                      placeholder="Tell us about your trademark needs..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      required
                      className="border-gray-300 mb-2 bg-white focus:border-[#D4A574] focus:ring-[#D4A574] resize-none"
                      data-testid="hero-form-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary text-[#D4A574]"
                    disabled={loading}
                    data-testid="hero-form-submit"
                  >
                    {loading ? 'Submitting...' : 'Register My Trademark'}
                  </Button>
                </form>

                {/* Trust badges */}
                {/* <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center space-x-1">
                    <Shield className="text-[#06214c]" size={16} />
                    <span className="text-xs text-[#D4A574]">Secure</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300"></div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="text-[#06214c]" size={16} />
                    <span className="text-xs text-[#D4A574]">Verified</span>
                  </div>
                  <div className="w-px h-4 bg-gray-300"></div>
                  <div className="flex items-center space-x-1">
                    <Award className="text-[#06214c]" size={16} />
                    <span className="text-xs text-[#D4A574]">Trusted</span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section with Image */}
      <section className="section bg-white" data-testid="info-section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-stretch">
            {/* Text Content */}
            <div className="order-2 lg:order-1 flex flex-col justify-between">
              <div>
                <div className="inline-block bg-[#06214c]/10 px-6 py-3 rounded-full mb-6">
                  <span className="text-[#06214c] font-semibold text-lg">Why Trademark Protection Matters</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-6" style={{ fontFamily: 'Playfair Display' }}>
                  Your Brand is Your Most Valuable Asset
                </h2>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                  In today's competitive marketplace, protecting your brand identity is not just important—it's essential. A registered trademark gives you exclusive rights to your brand name, logo, and slogan across the United States.
                </p>
                <div className="space-y-4 mb-8">
                  {[
                    {
                      icon: '🔒',
                      title: 'Exclusive Rights',
                      text: 'Prevent others from using similar marks in your industry'
                    },
                    {
                      icon: '💼',
                      title: 'Business Value',
                      text: 'Increase your company valuation and attract investors'
                    },
                    {
                      icon: '⚖️',
                      title: 'Legal Protection',
                      text: 'Enforce your rights in federal court against infringers'
                    },
                    {
                      icon: '🌟',
                      title: 'Brand Recognition',
                      text: 'Build consumer trust and brand loyalty nationwide'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start space-x-4 p-5 bg-[#FFF5E6] rounded-2xl hover:shadow-lg transition-shadow duration-300">
                      <div className="text-4xl flex-shrink-0">{item.icon}</div>
                      <div>
                        <h4 className="text-xl font-bold text-[#06214c] mb-1" style={{ fontFamily: 'Playfair Display' }}>
                          {item.title}
                        </h4>
                        <p className="text-gray-600 text-lg">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setShowDialog(true)}
                className="btn-primary w-full text-center text-xl py-6 mt-auto max-[360px]:text-sm max-[360px]:py-3"
              >
                Start Your Protection Today
              </button>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2">
              <div className="relative h-full">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl h-full min-h-[600px]">
                  <img 
                    src="/assets/img/photo-1521737852567-6949f3f9f2b5.jpg" 
                    alt="Professional team collaboration"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/20 to-transparent"></div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#D4A574]/30 rounded-full blur-3xl"></div>
                <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#06214c]/20 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="section section-abstract-bg bg-[#FFF5E6]" data-testid="features-section">
        <div className="container mx-auto">
          <div className={`text-center mb-16 ${featuresVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Choose Us
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              We provide comprehensive trademark services with unmatched expertise and dedication.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:grid max-[664px]:flex max-[664px]:gap-6 max-[664px]:overflow-x-auto max-[664px]:snap-x max-[664px]:scroll-smooth max-[664px]:px-8 max-[664px]:-mx-8 max-[664px]:pb-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ${featuresVisible ? 'animate-scale' : 'opacity-0'} animate-stagger-${index + 1} max-[664px]:flex-shrink-0 max-[664px]:w-[280px] max-[664px]:snap-start`}
                  data-testid={`feature-${index}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                        <Icon className="text-[#06214c]" size={24} />
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section - Creative Design */}
      <section ref={servicesRef} className="section bg-white section-abstract-shapes relative overflow-hidden" data-testid="services-section">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A574]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#06214c]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          <div className={`text-center mb-16 ${servicesVisible ? 'animate-fade-up' : 'opacity-0'}`}>
            <div className="inline-block mb-4">
              <span className="text-[#D4A574] font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Our Services
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Comprehensive solutions to protect and grow your business.
            </p>
          </div>

          <div className="space-y-8 max-[576px]:space-y-0 max-[576px]:flex max-[576px]:gap-6 max-[576px]:overflow-x-auto max-[576px]:snap-x max-[576px]:scroll-smooth max-[576px]:px-8 max-[576px]:-mx-8 max-[576px]:pb-4">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`group relative ${index % 2 === 0 ? '' : 'md:flex-row-reverse'} flex flex-col md:flex-row items-center gap-8 cursor-pointer max-[576px]:flex-shrink-0 max-[576px]:w-[300px] max-[576px]:snap-start ${servicesVisible ? (index % 2 === 0 ? 'animate-fade-left' : 'animate-fade-right') : 'opacity-0'} animate-stagger-${index + 1}`}
                data-testid={`service-${index}`}
              >
                {/* Image Side */}
                <div className="w-full md:w-1/2 relative">
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    {/* Number badge */}
                    <div className="absolute top-6 left-6 z-20 w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-2xl flex items-center justify-center shadow-lg rotate-12 group-hover:rotate-0 transition-transform duration-300">
                      <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                        {index + 1}
                      </span>
                    </div>
                    
                    <div className="relative h-80 max-[576px]:h-40 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/40 via-transparent to-[#D4A574]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </div>
                  
                  {/* Decorative element */}
                  <div className={`hidden md:block absolute -bottom-4 ${index % 2 === 0 ? '-right-4' : '-left-4'} w-32 h-32 bg-[#D4A574]/20 rounded-full blur-2xl group-hover:blur-3xl transition-all duration-500`}></div>
                </div>

                {/* Content Side */}
                <div className="w-full md:w-1/2">
                  <div className="relative p-8 md:p-12 max-[576px]:p-4">
                    {/* Background decoration */}
                    <div className="absolute inset-0 bg-white rounded-3xl shadow-lg transform group-hover:scale-105 transition-transform duration-300"></div>
                    
                    <div className="relative z-10">
                      <h3 className="text-3xl md:text-4xl font-bold text-[#06214c] mb-4 max-[576px]:text-xl max-[576px]:mb-2" style={{ fontFamily: 'Playfair Display' }}>
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed text-lg max-[576px]:text-sm max-[576px]:mb-2">
                        {service.description}
                      </p>
                      
                      {/* Feature bullets */}
                      <div className="flex flex-wrap gap-3 mb-6 max-[576px]:hidden">
                        {index === 0 && (
                          <>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Fast Filing</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Expert Review</span>
                            </div>
                          </>
                        )}
                        {index === 1 && (
                          <>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Legal Protection</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Fast Registration</span>
                            </div>
                          </>
                        )}
                        {index === 2 && (
                          <>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">LLC & Corp</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Tax Benefits</span>
                            </div>
                          </>
                        )}
                        {index === 3 && (
                          <>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">Brand Shield</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-[#FFF5E6] px-4 py-2 rounded-full">
                              <CheckCircle className="text-[#06214c]" size={16} />
                              <span className="text-sm text-[#2C1810] font-medium">A+ Content</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="inline-flex items-center text-[#06214c] font-bold text-lg group-hover:text-[#A0522D] transition-colors max-[576px]:text-sm">
                        Explore
                        <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" size={22} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-Page CTA Banner */}
      <section className="section relative overflow-hidden bg-gradient-to-r from-[#06214c] to-[#A0522D]" data-testid="mid-cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#D4A574] rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Don't Wait to Protect Your Brand
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Every day without trademark protection is a day your brand is at risk. Someone could file for your name before you do. Start your registration now and secure your business future.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-8">
              <div className="flex items-center space-x-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={24} />
                </div>
                <span className="text-lg font-semibold">24-Hour Filing</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={24} />
                </div>
                <span className="text-lg font-semibold">98% Success Rate</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <CheckCircle size={24} />
                </div>
                <span className="text-lg font-semibold">Money-Back Guarantee</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button
                onClick={() => setShowDialog(true)}
                className="bg-white text-[#06214c] border-2 border-[#ffffff] px-10 py-3 rounded-full font-bold text-xl hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-2xl max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Get Started Now
              </button>
              <button
                
                className="js-chat border-2 border-[#ffffff] bg-trasparent text-white px-10 py-3 rounded-full font-bold text-xl hover:bg-white hover:text-[#06214c] transition-all transform hover:scale-105 max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="section bg-white" data-testid="industries-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've helped thousands of businesses across diverse industries protect their brands
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 max-[664px]:flex max-[664px]:gap-6 max-[664px]:overflow-x-auto max-[664px]:snap-x max-[664px]:scroll-smooth max-[664px]:px-8 max-[664px]:-mx-8 max-[664px]:pb-4">
            {[
              { icon: '🍔', name: 'Food & Beverage', count: '2,500+' },
              { icon: '👕', name: 'Fashion & Apparel', count: '1,800+' },
              { icon: '💻', name: 'Technology', count: '1,200+' },
              { icon: '🏥', name: 'Healthcare', count: '950+' },
              { icon: '🏋️', name: 'Fitness & Wellness', count: '800+' },
              { icon: '🏠', name: 'Real Estate', count: '750+' },
              { icon: '🎨', name: 'Creative Services', count: '650+' },
              { icon: '📚', name: 'Education', count: '600+' },
              { icon: '🎵', name: 'Entertainment', count: '550+' },
              { icon: '🚗', name: 'Automotive', count: '500+' },
              { icon: '✈️', name: 'Travel & Tourism', count: '450+' },
              { icon: '📱', name: 'E-Commerce', count: '900+' }
            ].map((industry, index) => (
              <div 
                key={index}
                className="group relative bg-gradient-to-br from-[#FFF5E6] to-white rounded-3xl p-8 text-center shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#D4A574]/20 max-[664px]:flex-shrink-0 max-[664px]:w-[280px] max-[664px]:snap-start"
              >
                <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {industry.icon}
                </div>
                <h3 className="text-xl font-bold text-[#06214c] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  {industry.name}
                </h3>
                <p className="text-gray-600 font-semibold">{industry.count} Trademarks</p>
                <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      

      {/* Pricing Section */}
      <PricingTabs />

      {/* Testimonials Slider */}
      <div className="section-abstract-bg">
        <TestimonialSlider autoSlide={true} />
      </div>

      {/* Our Guarantees Section */}
      <section className="section bg-white" data-testid="guarantees-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Our Commitment to You
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We stand behind our services with industry-leading guarantees and support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-[576px]:flex max-[576px]:gap-6 max-[576px]:overflow-x-auto max-[576px]:snap-x max-[576px]:scroll-smooth max-[576px]:px-8 max-[576px]:-mx-8 max-[576px]:pb-4">
            {[
              {
                icon: '💰',
                title: 'Money-Back Guarantee',
                description: 'If we cannot file your trademark for any reason, you receive a full refund. No questions, no hassle.'
              },
              {
                icon: '⚡',
                title: '24-Hour Filing',
                description: 'We file your trademark application within 24 hours of receiving your completed information.'
              },
              {
                icon: '🛡️',
                title: 'Lifetime Support',
                description: 'Enjoy unlimited consultations and support for the lifetime of your trademark registration.'
              },
              {
                icon: '📱',
                title: 'Real-Time Updates',
                description: 'Track your application status 24/7 through our client portal with instant notifications.'
              },
              {
                icon: '👨‍💼',
                title: 'Dedicated Expert',
                description: 'Your personal trademark specialist guides you through every step of the registration process.'
              },
              {
                icon: '✅',
                title: '98% Success Rate',
                description: 'Our expert team has successfully registered over 10,000 trademarks with a 98% approval rate.'
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="group relative bg-gradient-to-br from-[#FFF5E6] to-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-300 border border-[#D4A574]/20 max-[576px]:flex-shrink-0 max-[576px]:w-[280px] max-[576px]:snap-start"
              >
                <div className="text-6xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  {item.description}
                </p>
                <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/5 to-[#D4A574]/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-abstract-bg" data-testid="faq-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to the most common questions about trademark registration
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: 'How long does the trademark registration process take?',
                  answer: 'The entire process typically takes 8-12 months from filing to approval. However, we file your application within 24 hours of receiving your information, and you\'ll receive a filing receipt from the USPTO within a few days.'
                },
                {
                  question: 'What can I trademark?',
                  answer: 'You can trademark brand names, logos, slogans, product names, and service names. The mark must be distinctive and used in commerce. Our team will help you determine if your mark qualifies for trademark protection.'
                },
                {
                  question: 'Do I need a trademark search before filing?',
                  answer: 'Yes, we highly recommend a comprehensive trademark search before filing. This helps identify potential conflicts with existing trademarks and increases your chances of approval. Our trademark search service is included in our Enhanced and Deluxe packages.'
                },
                {
                  question: 'What happens if my trademark is rejected?',
                  answer: 'If your trademark application receives an office action or rejection, our expert team will work with you to address the issues. We provide comprehensive support throughout the entire process, including responses to office actions at no additional cost.'
                },
                {
                  question: 'Can I trademark a name that\'s similar to an existing trademark?',
                  answer: 'It depends on several factors including the industry, goods/services offered, and likelihood of confusion. Our trademark attorneys will conduct a thorough analysis and advise you on the best course of action during your free consultation.'
                },
                {
                  question: 'What\'s the difference between ® and ™?',
                  answer: 'The ® symbol can only be used after your trademark is officially registered with the USPTO. The ™ symbol can be used immediately to indicate you\'re claiming trademark rights, even before registration is complete.'
                }
              ].map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-2xl shadow-lg border border-[#D4A574]/20 px-8 overflow-hidden"
                >
                  <AccordionTrigger className="text-2xl font-bold text-[#06214c] hover:text-[#A0522D] py-6 hover:no-underline" style={{ fontFamily: 'Playfair Display' }}>
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-lg leading-relaxed pb-6 pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section with Background */}
      <section 
        className="section relative overflow-hidden mb-0" 
        data-testid="cta-section"
        style={{ paddingBottom: '100px' }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Join thousands of satisfied clients who have secured their trademarks with us.
          </p>
           <button
                onClick={() => setShowDialog(true)}
                className="bg-white text-[#06214c] px-10 py-3 rounded-full font-bold text-xl hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-2xl max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Get Started Now
              </button>
        </div>
      </section>

      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="home" packageTitle="Essentials" packageAmount={45} tmSignUpType={1} />
      <Footer />
    </div>
  );
}